/**
 * The flight in six numbers, above the map and the inspector both — it describes
 * the flight, not the pane, which is why it spans them.
 *
 * Three rules it does not break (`docs/design/aether-ux.md` §6):
 *   · a slot with no value is not rendered at all and the rest close up. A strip
 *     full of em-dashes teaches nothing, and a spinner where a number will be
 *     teaches less;
 *   · derived values are ice, values the pilot typed over are `--ae-fg` — the
 *     same legend the form and the NavLog use, with no exception here;
 *   · it never announces. The banners are the announcement channel, and a strip
 *     that re-reads itself every time a wind arrives is a strip nobody can use
 *     with a screen reader on.
 */
import { useMemo } from 'react';
import { useT, type T } from '../i18n';
import { useField, usePlanRev, usePlanStore, useWarnings } from '../planContext';
import { LEG_NUMBERS } from '../fields';
import type { Severity } from './severity';

/** German thousands: a narrow no-break space, in headers and prose only. */
const groupThousands = (n: number): string =>
  Math.round(n).toLocaleString('de-DE').replace(/\./g, ' ');

const num = (s: string): number | null => {
  if (!s) return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** `87 NM`, `8.4 NM` — a decimal only where it changes the answer. */
export function formatDistance(raw: string): string | null {
  const n = num(raw);
  if (n === null) return null;
  return n < 10 ? String(Math.round(n * 10) / 10) : groupThousands(n);
}

/** minutes → `0:52`, `12:05` */
export function formatDuration(raw: string): string | null {
  const n = num(raw);
  if (n === null) return null;
  const total = Math.round(n);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h + ':' + String(m).padStart(2, '0');
}

/** the sheet stores thousands of feet with one decimal; a pilot compares feet */
export function formatSafeAlt(raw: string): string | null {
  const n = num(raw);
  if (n === null) return null;
  return groupThousands(n * 1000);
}

interface SlotProps {
  label?: string;
  value: string;
  unit?: string;
  derived: boolean;
  severity?: Severity | undefined;
  title?: string;
  /** a registration is a name, not a quantity — it is set in Sans */
  mono?: boolean;
}

function Slot({ label, value, unit, derived, severity, title, mono = true }: SlotProps) {
  const cls =
    'ae-summary-slot' +
    (derived ? '' : ' typed') +
    (severity === 'warning' ? ' warning' : severity === 'caution' ? ' caution' : '');
  return (
    <span className={cls} {...(title ? { title } : {})}>
      {label ? <span className="ae-summary-label">{label}</span> : null}
      <span className={'ae-summary-value' + (mono ? ' ae-num' : '')}>{value}</span>
      {unit ? <span className="ae-summary-unit">{unit}</span> : null}
    </span>
  );
}

/**
 * The highest Sicherheitshöhe on the plan, in the sheet's own unit, and whether
 * the leg it came from is a value Aether worked out or one the pilot decided.
 */
export function maxSafeAlt(
  read: (id: string) => string,
  isDerived: (id: string) => boolean = () => true,
): { value: string; derived: boolean } {
  let best: number | null = null;
  let derived = true;
  for (const j of LEG_NUMBERS) {
    const id = 'leg' + j + '_safealt';
    const n = num(read(id));
    if (n !== null && (best === null || n > best)) {
      best = n;
      derived = isDerived(id);
    }
  }
  return { value: best === null ? '' : String(best), derived };
}

export function SummaryStrip({ actions }: { actions?: React.ReactNode }) {
  const t: T = useT();
  const store = usePlanStore();
  const rev = usePlanRev();
  const { warnings } = useWarnings();

  const dist = useField('total_dist');
  const time = useField('total_time');
  const eta = useField('eta');
  const need = useField('fuel_mindest_l');
  const have = useField('fuel_vorrat_l');
  const kennz = useField('lfz_kennz');
  const muster = useField('lfz_muster');

  const safealt = useMemo(
    () => maxSafeAlt(id => store.val(id), id => store.derivedSource(id) !== ''),
    [store, rev],
  );

  const codes = useMemo(() => new Set(warnings.map(w => w.code)), [warnings]);
  const etaSeverity: Severity | undefined = codes.has('eta-after-sunset') || codes.has('eta-next-day')
    ? 'warning'
    : codes.has('eta-near-sunset')
      ? 'caution'
      : undefined;
  const timeSeverity: Severity | undefined = codes.has('exceeds-safe-time') ? 'warning' : undefined;
  const needN = num(need.value);
  const haveN = num(have.value);
  const fuelSeverity: Severity | undefined =
    needN !== null && haveN !== null && needN > haveN ? 'warning' : undefined;

  const distText = formatDistance(dist.value);
  const timeText = formatDuration(time.value);
  const fuelText = need.value || have.value ? (need.value || '—') + ' / ' + (have.value || '—') : null;
  const shText = formatSafeAlt(safealt.value);

  return (
    <div className="ae-summary" aria-label={t('summary.aria')} aria-live="off">
      {distText ? <Slot value={distText} unit="NM" derived={dist.auto} /> : null}
      {timeText ? <Slot value={timeText} derived={time.auto} severity={timeSeverity} /> : null}
      {eta.value ? (
        <Slot label={t('summary.eta')} value={eta.value + 'Z'} derived={eta.auto} severity={etaSeverity} />
      ) : null}
      {fuelText ? (
        <Slot label={t('summary.fuel')} value={fuelText} unit="l" derived={need.auto} severity={fuelSeverity} />
      ) : null}
      {shText ? (
        <Slot label={t('summary.sh')} value={shText} unit="ft" derived={safealt.derived} />
      ) : null}
      {kennz.value ? (
        <Slot
          value={kennz.value}
          mono={false}
          derived={kennz.auto || kennz.derived !== ''}
          {...(muster.value ? { title: muster.value } : {})}
        />
      ) : null}
      {actions ? <div className="ae-summary-actions">{actions}</div> : null}
    </div>
  );
}
